import "@/assets/css/responsive.css";
import DateFilterButton from "@/components/layouts/DateFilterButton";
import PageLoader from "@/components/loader/PageLoader";
import Spinner from "@/components/loader/Spinner";
import { Order } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, startOfDay, endOfDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import OrderDetails from "./OrderDetails";
import { toast } from "sonner";

const OrderHistory: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [activeTab, setActiveTab] = useState("paid");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [axiosInstance, isFetching] = useAxiosInstance();
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [branchTimezone, setBranchTimezone] = useState<string>("UTC");
  const prevOrdersRef = useRef<Order[]>([]);
  const [swRegistration, setSwRegistration] =
    useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    const storedData = localStorage.getItem("user_data");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      const { selectedBranch } = parsedData;
      setSelectedBranch(selectedBranch.id);
      setBranchTimezone(selectedBranch.timezone || "UTC");
    }
  }, []);

  const formatToUTC = (
    date: Date | null,
    isEndOfDay: boolean = false,
  ): string => {
    if (!date) return "";

    // Convert to branch timezone first
    const zonedDate = toZonedTime(date, branchTimezone);

    // Get start or end of day in branch timezone
    const localDate = isEndOfDay ? endOfDay(zonedDate) : startOfDay(zonedDate);

    // Convert local branch time directly to UTC timestamp
    return new Date(
      localDate.toLocaleString("en-US", {
        timeZone: branchTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
    )
      .toISOString()
      .slice(0, 19);
  };

  const formatToLocalBranchTime = (isoString: string) => {
    const date = new Date(isoString);
    const zonedDate = toZonedTime(date, branchTimezone);
    return format(zonedDate, "yyyy-MM-dd HH:mm:ss");
  };

  // Replace the existing formatToCST function with this one
  const formatDateTime = (isoString: string) => {
    return formatToLocalBranchTime(isoString);
  };

  const fetchOrders = async (status: string, byInterval: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const start = formatToUTC(startDate);
      const end = formatToUTC(endDate, true);
      const baseUrl = `api/v1/order/?branch=${selectedBranch}&created_at_after=${start}&created_at_before=${end}&sales_channel=Online`;

      let data: { results: Order[]; count: number } = {
        results: [],
        count: 0,
      };

      if (status === "paid") {
        // Get preparing and paid orders
        const url = `${baseUrl}&page=${currentPage}&page_size=${pageSize}&payment_status=Paid`;
        const response = await axiosInstance.get(url);

        if (!response.status.toString().startsWith("2")) {
          throw new Error("Failed to fetch orders");
        }

        data = response.data;

        console.log(data.results);
        setOrders(data.results);
        setTotalOrders(data.count);
        setTotalPages(Math.ceil(data.count / pageSize));
      } else if (status === "completed") {
        // Get completed and paid orders
        const url = `${baseUrl}&page=${currentPage}&page_size=${pageSize}&status=Completed&payment_status=Paid`;
        const response = await axiosInstance.get(url);

        if (!response.status.toString().startsWith("2")) {
          throw new Error("Failed to fetch orders");
        }

        data = response.data;
        setOrders(data.results);
        setTotalOrders(data.count);
        setTotalPages(Math.ceil(data.count / pageSize));
      } else if (status === "refund") {
        const refundStatuses = [
          "Voided",
          "Refunded",
          "Partially Refunded",
          "Partially Paid",
        ];
        const refundPromises = refundStatuses.map((refundStatus) =>
          axiosInstance.get(
            `${baseUrl}&payment_status=${encodeURIComponent(refundStatus)}`,
          ),
        );
        const responses = await Promise.all(refundPromises);
        const allRefundOrders = responses.flatMap(
          (response) => response.data.results,
        );

        const sortedRefundOrders = allRefundOrders.sort((a, b) => {
          const aNum = parseInt(a.order_number.replace("#", ""));
          const bNum = parseInt(b.order_number.replace("#", ""));
          return bNum - aNum;
        });

        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        data.results = sortedRefundOrders.slice(startIndex, endIndex);
        data.count = sortedRefundOrders.length;
        setOrders(data.results);
        setTotalOrders(data.count);
        setTotalPages(Math.ceil(data.count / pageSize));
      } else if (status === "PostPaid") {
        const url = `${baseUrl}&page=${currentPage}&page_size=${pageSize}&payment_status=PostPaid`;
        const response = await axiosInstance.get(url);

        if (!response.status.toString().startsWith("2")) {
          throw new Error("Failed to fetch orders");
        }

        data = response.data;
        setOrders(data.results);
        setTotalOrders(data.count);
        setTotalPages(Math.ceil(data.count / pageSize));
      } else if (status === "Scheduled") {
        const url = `${baseUrl}&page=${currentPage}&page_size=${pageSize}&status=Scheduled`;
        const response = await axiosInstance.get(url);

        if (!response.status.toString().startsWith("2")) {
          throw new Error("Failed to fetch orders");
        }

        data = response.data;
        setOrders(data.results);
        setTotalOrders(data.count);
        setTotalPages(Math.ceil(data.count / pageSize));
      } else if (status === "AllOrders") {
        const url = `${baseUrl}&page=${currentPage}&page_size=${pageSize}`;
        const response = await axiosInstance.get(url);

        if (!response.status.toString().startsWith("2")) {
          throw new Error("Failed to fetch orders");
        }

        data = response.data;
        setOrders(data.results);
        setTotalOrders(data.count);
        setTotalPages(Math.ceil(data.count / pageSize));
      }

      if (byInterval) {
        checkForNewOrders(data.results);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const playBeepSound = () => {
    console.log("Playing beep sound");
    const beep = new Audio(
      "https://www.soundjay.com/buttons/sounds/beep-02.mp3",
    );
    beep.play().catch((error) => console.error("Audio play error:", error));
  };

  const showNotification = (title: string, body: string) => {
    if (swRegistration) {
      swRegistration.showNotification(title, {
        body,
        icon: "/logo192.png",
        badge: "/logo192.png",
      });
    }
  };

  const notifyUser = () => {
    playBeepSound();
    if ("vibrate" in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
    showNotification("New Order", "You have a new order!");
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
  };

  const checkForNewOrders = (currentOrders: Order[]) => {
    if (currentOrders.length > 0 && prevOrdersRef.current.length > 0) {
      const newOrders = currentOrders.filter(
        (order) =>
          !prevOrdersRef.current.some((prevOrder) => prevOrder.id === order.id),
      );
      if (newOrders.length > 0) {
        notifyUser();
      }
    }
    prevOrdersRef.current = currentOrders;
  };

  useEffect(() => {
    if (selectedBranch) {
      fetchOrders(activeTab);
    } else {
      setLoading(false);
    }
  }, [selectedBranch, activeTab, currentPage, pageSize, startDate, endDate]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedBranch) {
        fetchOrders(activeTab, true);
      }
    }, 180000);

    return () => clearInterval(interval);
  }, [selectedBranch, activeTab, currentPage, pageSize, startDate, endDate]);

  useEffect(() => {
    if (
      "Notification" in window &&
      Notification.permission !== "denied" &&
      Notification.permission !== "granted"
    ) {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          localStorage.setItem("notifications_permission", "granted");
        } else {
          localStorage.setItem("notifications_permission", "denied");
        }
      });
    }
  }, []);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        setSwRegistration(registration);
      });
    }
  }, []);

  const handleStatusChange = async (status: string) => {
    if (selectedOrder) {
      try {
        const response = await axiosInstance.post(
          `api/v1/order/${selectedOrder.id}/update_order_status/`,
          { status },
        );

        if (!response.status.toString().startsWith("2")) {
          throw new Error("Failed to update order status");
        }

        if (activeTab === "paid" && status === "Completed") {
          // First, update total counts
          const newTotalOrders = totalOrders - 1;
          const newTotalPages = Math.ceil(newTotalOrders / pageSize);

          setTotalOrders(newTotalOrders);
          setTotalPages(newTotalPages);

          // Remove the order from the current list
          const updatedOrders = orders.filter(
            (order) => order.id !== selectedOrder.id,
          );

          // If this removes the last item from the current page
          // AND we're not on the first page
          // AND the current page would now be beyond our total pages
          if (
            updatedOrders.length === 0 &&
            currentPage > 1 &&
            currentPage > newTotalPages
          ) {
            // Fetch the previous page
            const start = formatToUTC(startDate);
            const end = formatToUTC(endDate, true);
            const url = `api/v1/order/?branch=${selectedBranch}&created_at_after=${start}&created_at_before=${end}&page=${currentPage - 1}&page_size=${pageSize}&status=Preparing&payment_status=Paid`;

            const newPageResponse = await axiosInstance.get(url);
            setOrders(newPageResponse.data.results);
            setCurrentPage(currentPage - 1);
          } else {
            // Just update with filtered orders
            setOrders(updatedOrders);
          }

          toast.success(
            `Order ${selectedOrder.order_number} has been moved to completed orders`,
          );
        } else {
          setOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.id === selectedOrder.id
                ? { ...order, fulfillment_status: status }
                : order,
            ),
          );
          toast.success(
            `Order ${selectedOrder.order_number} status updated to ${status}`,
          );
        }

        setSelectedOrder(null);
      } catch (error: unknown) {
        console.error("Error updating order status:", error);
        toast.error(
          `Failed to update order status: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    }
  };

  const getOrderStatusClass = (paymentStatus: string) => {
    switch (paymentStatus) {
      case "Paid":
        return "border-l-4 border-green-500";
      case "PostPaid":
        return "border-l-4 border-yellow-500";
      case "Unpaid":
        return "border-l-4 border-red-500";
      default:
        return "border-l-4 border-gray-500";
    }
  };

  const formatToCST = (isoString: string) => {
    const date = new Date(isoString);
    const zonedDate = toZonedTime(date, "America/Chicago");
    return format(zonedDate, "yyyy-MM-dd HH:mm:ss");
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="min-h-screen p-4">
        <div className="mb-4 flex flex-col items-center justify-between space-y-2 sm:flex-row sm:space-y-0">
          <div className="flex space-x-4">
            <select
              value={activeTab}
              onChange={(e) => {
                setActiveTab(e.target.value);
                setCurrentPage(1);
              }}
              className="w-48 rounded border-2 border-red-500 bg-white px-4 py-2 text-red-500 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="PostPaid">
                {t("back.management.order.postpaidorders")}
              </option>
              <option value="paid">
                {t("back.management.order.paidorders")}
              </option>
              <option value="completed">
                {t("back.management.order.completedorders")}
              </option>
              <option value="refund">
                {t("back.management.order.refundorders")}
              </option>
              <option value="Scheduled">
                {t("back.management.order.scheduledorders")}
              </option>
              <option value="AllOrders">
                {t("back.management.order.allorders")}
              </option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-col items-center justify-between space-y-2 sm:flex-row sm:space-y-0">
          <div className="mb-4 flex w-full flex-col items-center space-y-2 sm:flex-row sm:space-x-4 sm:space-y-0">
            <DateFilterButton
              onStartDateChange={(date) => {
                setStartDate(date);
                setEndDate(null);
              }}
              onEndDateChange={(date) => setEndDate(date)}
              startDate={startDate}
              endDate={endDate}
              labelText="Filter by date"
            />
          </div>
        </div>
        {loading ? (
          <Spinner />
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : selectedBranch ? (
          <>
            {/* Desktop Table */}
            <div className="desktop-layout rounded-lg bg-white p-4 shadow">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.order.ordername")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.order.ordernumber")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.order.ordertype")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.order.status")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.order.table.status")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.order.saleschannel")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.order.total")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.order.tip")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.order.refundAmount")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {orders?.map((order) => (
                      <tr
                        key={order.id}
                        onClick={() => handleOrderClick(order)}
                      >
                        <td className="cursor-pointer whitespace-nowrap px-6 py-4 text-sm text-blue-500">
                          {order.order_name}
                        </td>
                        <td className="cursor-pointer whitespace-nowrap px-6 py-4 text-sm text-blue-500">
                          {order.order_number}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                              order.order_type.toLowerCase() === "pick up" ||
                              order.order_type.toLowerCase() === "pickup"
                                ? "bg-green-100 text-green-800"
                                : "text-black-800 bg-blue-100"
                            }`}
                          >
                            {order.order_type}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                              order.fulfillment_status === "Completed"
                                ? "bg-green-100 text-green-800"
                                : order.fulfillment_status === "Cancelled"
                                  ? "bg-red-100 text-red-800"
                                  : order.fulfillment_status === "Scheduled"
                                    ? "bg-gray-100 text-gray-800"
                                    : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {order.fulfillment_status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                              order.payment_status === "Paid"
                                ? "bg-green-100 text-green-800"
                                : order.payment_status === "PostPaid"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800"
                            }`}
                          >
                            {order.payment_status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                              order.sales_channel.toLowerCase() === "online"
                                ? "bg-blue-100 text-blue-800"
                                : order.sales_channel.toLowerCase() === "pos"
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-purple-100 text-purple-800"
                            }`}
                          >
                            {order.sales_channel}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          ${order.total_price}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          ${order.tip || "0.00"}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-500">
                          ${order.amount_refund || "0.00"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-between">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="rounded bg-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-400"
                >
                  {t("back.management.order.previous")}
                </button>
                <span>
                  {t("back.management.order.page")} {currentPage}{" "}
                  {t("back.management.order.of")} {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded bg-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-400"
                >
                  {t("back.management.order.next")}
                </button>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="ml-4 w-16 rounded border p-2"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="mobile-layout">
              <div className="grid grid-cols-1 gap-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className={`relative flex items-center justify-between rounded-lg bg-white p-4 shadow ${getOrderStatusClass(
                      order.payment_status,
                    )}`}
                    onClick={() => handleOrderClick(order)}
                  >
                    <div className="flex items-center">
                      <div>
                        <p className="font-bold text-gray-900">
                          {order.order_number}
                        </p>
                        <p className="text-gray-700">
                          {order.customer_name}{" "}
                          <span className="text-gray-500">
                            {order.phone_number}
                          </span>
                        </p>
                        <p>
                          <span className="font-semibold">
                            {t("back.management.order.ordername")}:
                          </span>{" "}
                          {order.order_name}
                        </p>
                        <p className="font-bold text-gray-900">
                          Total - ${order.total_price}
                        </p>
                        {order.payment_status === "Partially Refunded" && (
                          <p className="text-red-500">
                            Refunded - ${order.amount_refund}
                          </p>
                        )}
                        <p className="font-bold text-gray-900"></p>
                        <p className="text-gray-700">
                          {order.order_type}- {order.sales_channel}
                        </p>
                        <p className="text-gray-700">
                          {formatDateTime(order.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col items-center pt-4">
                <div className="mb-4">
                  <p className="text-sm text-gray-700">
                    {t("back.management.order.showing")}{" "}
                    <span className="font-medium">
                      {(currentPage - 1) * pageSize + 1}
                    </span>{" "}
                    {t("back.management.order.to")}{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, totalOrders)}
                    </span>{" "}
                    {t("back.management.order.of")}{" "}
                    <span className="font-medium">{totalOrders}</span>{" "}
                    {t("back.management.order.results")}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="p-2 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <div className="flex items-center space-x-2">
                    {getPageNumbers(currentPage, totalPages).map(
                      (page, index) => (
                        <button
                          key={index}
                          onClick={() =>
                            typeof page === "number" && setCurrentPage(page)
                          }
                          className={`rounded px-2 py-1 ${
                            currentPage === page
                              ? "bg-red-500 text-white"
                              : "bg-gray-200 text-gray-700"
                          }`}
                          disabled={typeof page !== "number"}
                        >
                          {page}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p>{t("back.management.order.selectBranch")}</p>
        )}
        {selectedOrder && (
          <OrderDetails
            isOpen={true}
            branchId={selectedBranch?.toString() || ""}
            onClose={() => setSelectedOrder(null)}
            order={selectedOrder}
            onStatusChange={handleStatusChange}
          />
        )}
      </div>
    </PageLoader>
  );
};

// Helper function to generate pagination numbers with ellipsis
const getPageNumbers = (currentPage: number, totalPages: number) => {
  const delta = 1;
  const range = [];
  for (
    let i = Math.max(2, currentPage - delta);
    i <= Math.min(totalPages - 1, currentPage + delta);
    i++
  ) {
    range.push(i);
  }
  if (currentPage - delta > 2) {
    range.unshift("...");
  }
  if (currentPage + delta < totalPages - 1) {
    range.push("...");
  }
  range.unshift(1);
  if (totalPages > 1) {
    range.push(totalPages);
  }
  return range;
};

export default OrderHistory;
